import { useState, useCallback, useMemo, useRef } from 'react'
import { supabase } from '../lib/supabase'
import { computeConditionalStopKeys, isConditionalStop } from '../lib/bracketDetection.js'
import { adaptOrders } from '../lib/orderAdapter.js'
import { groupSplitBracketOrders } from '../lib/bracketOrderGroups.js'

export { flattenChildStrategies } from '../lib/bracketDetection.js'

function normalizeAccountList(accounts) {
  return [...new Set(
    (Array.isArray(accounts) ? accounts : [])
      .map(value => (value == null ? '' : String(value).trim()))
      .filter(Boolean),
  )].sort((a, b) => a.localeCompare(b))
}

// EF-189: caller passes `trackedOrderIds` — the flattened set of every
// position_projects.schwab_order_ids entry for positions currently in
// scope. The hook joins each adapted order to its bracketGroupId via that
// array and collapses target + runner pairs into a single SPLIT_BRACKET row
// for display + cancel. Orders without a matching tracked id pass through
// unchanged so legacy nested-TRIGGER brackets and externally-placed orders
// still render and cancel correctly.
export function useWorkingOrders({ trackedOrderIds = null } = {}) {
  // `rawOrders` holds the un-grouped adapted list — every Schwab order is a
  // separate entry. `orders` (the public surface) is the grouped view that
  // collapses split-bracket pairs into one logical SPLIT_BRACKET row.
  const [rawOrders, setRawOrders] = useState([])
  const [loading, setLoading] = useState(false)
  const [ready, setReady] = useState(false)
  const [error, setError] = useState(null)
  const inFlightRefreshesRef = useRef(new Map())
  const setOrders = setRawOrders
  const orders = useMemo(
    () => groupSplitBracketOrders(rawOrders, trackedOrderIds || []),
    [rawOrders, trackedOrderIds],
  )

  // Full fetch — all accounts, all symbols (fallback)
  const fetchOrders = useCallback(async () => {
    setLoading(true)
    setReady(false)
    setError(null)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.access_token) throw new Error('Not authenticated')

      const res = await fetch('/api/schwab/orders', {
        headers: { Authorization: `Bearer ${session.access_token}` }
      })

      if (!res.ok) throw new Error(`Failed to fetch orders: ${res.status}`)
      const data = await res.json()
      setOrders(adaptOrders(data.orders || []))
      console.log(`[useWorkingOrders] fetchOrders returned ${(data.orders || []).length} orders`)
      setReady(true)
    } catch (err) {
      setError(err.message)
      setOrders([])
    } finally {
      setLoading(false)
    }
  }, [])

  /**
   * Lightweight refresh for a single symbol + specific accounts.
   * Updates schwab_positions and trades in the DB, returns fresh orders.
   */
  const refreshForSymbol = useCallback(async (symbol, accounts, { signal } = {}) => {
    if (!symbol) return null
    const accountList = normalizeAccountList(accounts)
    let url = `/api/schwab/refresh-position?symbol=${encodeURIComponent(symbol)}`
    if (accountList.length > 0) url += `&accounts=${accountList.join(',')}`

    const inFlightRefresh = inFlightRefreshesRef.current.get(url)
    if (inFlightRefresh) {
      if (!inFlightRefresh.signal?.aborted) return inFlightRefresh.promise
      inFlightRefreshesRef.current.delete(url)
    }

    if (orders.length === 0) setLoading(true)
    setError(null)
    const refreshPromise = (async () => {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.access_token) throw new Error('Not authenticated')

      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${session.access_token}` },
        signal,
      })

      if (!res.ok) throw new Error(`Failed to refresh: ${res.status}`)
      const data = await res.json()

      // EF-190: drop a late-resolving response for a now-stale symbol so it
      // can't overwrite state for the newer one.
      if (signal?.aborted) return null

      if (data.ordersComplete === false) {
        const failedAccounts = Array.isArray(data.orderFetchErrors)
          ? data.orderFetchErrors.map(err => String(err.accountNumber || '').slice(-4)).filter(Boolean)
          : []
        const scopedMessage = failedAccounts.length > 0
          ? `Incomplete Schwab orders snapshot for account(s): ${failedAccounts.join(', ')}`
          : 'Incomplete Schwab orders snapshot'
        setOrders(prev => {
          const upperSymbol = symbol.toUpperCase()
          const accountSet = accountList.length > 0 ? new Set(accountList) : null
          return prev.filter(o => {
            if (o.symbol !== upperSymbol) return true
            if (accountSet && !accountSet.has(String(o.accountNumber || '').trim())) return true
            return false
          })
        })
        setReady(false)
        setError(scopedMessage)
        return null
      }

      // Merge refreshed orders — replace orders for this symbol+accounts, keep others.
      // Adapt once here so callers reading the returned data (e.g. ActionPanel)
      // see the same normalized shape that ends up in component state.
      const adapted = data.orders ? adaptOrders(data.orders) : null
      if (adapted) {
        setOrders(prev => {
          const upperSymbol = symbol.toUpperCase()
          const accountSet = accountList.length > 0 ? new Set(accountList) : null
          const kept = prev.filter(o => {
            if (o.symbol !== upperSymbol) return true
            if (accountSet && !accountSet.has(String(o.accountNumber || '').trim())) return true
            return false
          })
          return [...kept, ...adapted]
        })
      }

      if (adapted) {
        console.log(`[useWorkingOrders] refreshForSymbol(${symbol}) returned ${adapted.length} orders`)
      }
      setReady(true)
      return adapted ? { ...data, orders: adapted } : data
    })()
      .catch((err) => {
        if (err?.name === 'AbortError') return null
        setError(err.message)
        return null
      })
      .finally(() => {
        if (inFlightRefreshesRef.current.get(url)?.promise === refreshPromise) {
          inFlightRefreshesRef.current.delete(url)
        }
        setLoading(false)
      })

    inFlightRefreshesRef.current.set(url, { promise: refreshPromise, signal })
    return refreshPromise
  }, [])

  // Orders actually working at Schwab right now — protecting live shares.
  const WORKING_STATUSES = new Set(['WORKING', 'ACCEPTED'])
  // Orders that exist but are waiting for a parent condition (bracket
  // children whose entry hasn't filled yet).
  const PENDING_STATUSES = new Set([
    'AWAITING_PARENT_ORDER', 'AWAITING_CONDITION',
    'AWAITING_STOP_CONDITION', 'PENDING_ACTIVATION', 'QUEUED', 'NEW',
  ])
  // Union for display/informational purposes.
  const ALL_ACTIVE_STATUSES = new Set([...WORKING_STATUSES, ...PENDING_STATUSES])
  const TERMINAL_STATUSES = new Set(['FILLED', 'EXPIRED', 'CANCELED', 'CANCELLED', 'REJECTED', 'REPLACED'])

  // ── EF-129: Memoized conditional stop keys for shared use ──────────
  // Identifies bracket-child stops from unfilled BUY parents (the orders
  // feed strips terminal statuses, so any BUY parent we still see is by
  // definition unfilled). Two API endpoints feed orders into state and
  // produce different shapes:
  //   • /api/schwab/orders — enrichOrder() sets hasChildren + childOrders
  //   • /api/schwab/refresh-position — passes raw childOrderStrategies
  //     through, possibly nested as TRIGGER → OCO → [STOP, LIMIT]
  // Detect both shapes; flatten the raw form via flattenChildStrategies.
  // Computed once per `orders` change, shared by getStopOrdersForSymbol
  // (filter logic, EF-127b) and WorkingOrdersPanel (display, EF-129).
  // EF-189: compute against rawOrders (each tranche independent). The
  // grouped public view replaces target + runner with one SPLIT_BRACKET
  // wrapper; conditional-stop detection needs the per-tranche walk.
  const conditionalStopKeys = useMemo(
    () => computeConditionalStopKeys(rawOrders, { terminalStatuses: TERMINAL_STATUSES }),
    [rawOrders],
  )

  /**
   * Returns stop orders for a symbol. By default only returns WORKING
   * standalone stops that are actively protecting current shares. Pass
   * `{ includeAwaitingParent: true }` to also include bracket children
   * whose parent entry hasn't filled yet (informational only — not safe
   * to REPLACE/CANCEL as a position stop).
   */
  const getStopOrdersForSymbol = useCallback((symbol, { includeAwaitingParent = false } = {}) => {
    if (!symbol) return []
    const upperSymbol = symbol.toUpperCase()
    const out = []

    if (conditionalStopKeys.size > 0) {
      console.log(`[getStopOrdersForSymbol] conditionalStopKeys:`, [...conditionalStopKeys])
    }

    // EF-189: walk rawOrders so each split-bracket tranche is evaluated
    // independently (the SPLIT_BRACKET wrapper would otherwise hide the
    // runner's standalone stop child).
    for (const o of rawOrders) {
      if (o.symbol !== upperSymbol) continue

      const isDirectStop = o.orderType === 'STOP' || o.orderType === 'STOP_LIMIT'
      const bracketStopChildren = Array.isArray(o.childOrders)
        ? o.childOrders.filter(c =>
          (c.orderType === 'STOP' || c.orderType === 'STOP_LIMIT') &&
          (c.computedStopPrice != null || c.stopPrice != null),
        )
        : []
      const isBracketWithStop = !!o.isBracket && (o.computedStopPrice != null || bracketStopChildren.length > 0)

      if (!isDirectStop && !isBracketWithStop) continue

      // ── BUY-side filter ──────────────────────────────────────────
      // Stop LOSS lines on the chart are always SELL-side. BUY-side
      // STOP orders are entries (e.g. On Strength buy-stops), not
      // protective stops. Filter them out regardless of whether
      // they're standalone or bracket parents.
      const primaryInstruction = o.side
      if (primaryInstruction === 'BUY' || primaryInstruction === 'BUY_TO_OPEN') continue

      // ── EF-125 + EF-127 + EF-127b: bracket-child filter for direct stops ──
      // Schwab returns bracket SELL-STOP children both nested inside
      // the parent AND as separate top-level orders. The top-level
      // copies can carry ANY non-terminal status — PENDING_ACTIVATION,
      // AWAITING_PARENT_ORDER, or even WORKING — regardless of whether
      // the parent has filled. Schwab's status field is unreliable.
      //
      // The only reliable test: does an unfilled BUY bracket parent
      // with a matching child stop price still exist in the orders
      // feed? The pre-scan above collects those keys; if a direct
      // stop's (symbol, account, price) matches, it's conditional and
      // must stay excluded. Otherwise let it through — Schwab keeps
      // post-fill child stops pinned at PENDING_ACTIVATION but they
      // are live working stops protecting current shares.
      if (isDirectStop && !includeAwaitingParent) {
        if (isConditionalStop(o, o.accountNumber, conditionalStopKeys)) continue
      }

      // ── Status filtering ──────────────────────────────────────────
      // Direct standalone STOP/STOP_LIMIT: accept ANY non-terminal status
      // EXCEPT the await-parent set above. The API already strips
      // FILLED/CANCELED/EXPIRED/REJECTED/REPLACED, so anything else
      // (ACCEPTED, QUEUED, NEW, WORKING) is a live standalone stop.
      if (!isDirectStop && isBracketWithStop) {
        // Bracket parents: use the status filter to distinguish working
        // (entry has filled → children are active) vs pending (entry not
        // yet filled → children protect future shares only).
        const allowedStatuses = includeAwaitingParent ? ALL_ACTIVE_STATUSES : WORKING_STATUSES
        if (!allowedStatuses.has(o.status)) continue

        // Skip bracket parents with unfilled BUY entry legs — their child
        // stops protect future shares, not current ones.
        if (!includeAwaitingParent) {
          const instruction = o.side
          if (instruction === 'BUY' || instruction === 'BUY_TO_OPEN') continue
        }
      }

      // ── Extract stop details ──────────────────────────────────────
      const stopRows = isBracketWithStop && bracketStopChildren.length > 0
        ? bracketStopChildren
        : [null]

      for (const stopChild of stopRows) {
        const stopOrderId = String(stopChild?.orderId || o.orderId || o.parentOrderId || '')
        const stopPrice = stopChild?.computedStopPrice ?? stopChild?.stopPrice ?? o.stopPrice ?? o.computedStopPrice ?? null
        const quantity = stopChild?.quantity ?? stopChild?.remainingQuantity ?? o.quantity ?? o.remainingQuantity
        const instruction = stopChild?.side || o.side

        out.push({
          accountNumber: o.accountNumber,
          orderId: stopOrderId,
          existingOrderId: stopOrderId,
          parentOrderId: String(o.orderId || ''),
          stopPrice,
          quantity,
          cancelable: stopChild?.cancelable ?? o.cancelable,
          editable: stopChild?.editable ?? o.editable,
          isBracket: !!o.isBracket,
          status: stopChild?.status || o.status,
          instruction,
        })
      }
    }

    console.log(`[getStopOrdersForSymbol] ${upperSymbol} includeAwaitingParent=${includeAwaitingParent} → ${out.length} stops`,
      out.map(s => ({ acct: s.accountNumber?.slice(-4), price: s.stopPrice, status: s.status, bracket: s.isBracket, instruction: s.instruction })))

    return out
  }, [rawOrders, conditionalStopKeys])

  return { orders, rawOrders, loading, ready, error, fetchOrders, refreshForSymbol, getStopOrdersForSymbol, conditionalStopKeys }
}
